import { AlertCircle, Clock } from "lucide-react";
import {
  isAwaitingReviewStatus,
  isRejectedStatus,
  type EvidenceStatus,
} from "../../../models/evidenceItem";

type TeachingReviewNoticeProps = {
  isReported: boolean;
  status: EvidenceStatus;
};

type Notice = {
  color: string;
  description: string;
  Icon: typeof Clock;
  title: string;
};

const getNotice = (status: EvidenceStatus): Notice | null => {
  if (isAwaitingReviewStatus(status)) {
    return {
      color: "var(--cle-warning-solid)",
      Icon: Clock,
      title: "Undervisningen afventer sekretariatets godkendelse.",
      description:
        "Sekretariatet gennemgår undervisning manuelt. Indtil undervisningen er godkendt, kan lektionerne ikke vælges i en indberetning. Status opdateres her på siden, når sekretariatet har behandlet den. Har du spørgsmål, kan du skrive til sekretariatet længere nede på siden.",
    };
  }

  if (isRejectedStatus(status)) {
    return {
      color: "var(--cle-danger)",
      Icon: AlertCircle,
      title: "Undervisningen er afvist af sekretariatet.",
      description:
        "Lektionerne kan ikke indgå i en indberetning. Se sekretariatets besked længere nede på siden. Mener du, at det er en fejl, kan du skrive til sekretariatet dér, så bliver undervisningen vurderet igen.",
    };
  }

  return null;
};

/**
 * Forklarer, hvorfor undervisning ikke kan indberettes endnu. Advokatsamfundet screener
 * al undervisning manuelt, så ny undervisning starter som "Afventer sekretariat" i stedet
 * for at være godkendt med det samme, og uden en forklaring ligner det en fejl.
 */
const TeachingReviewNotice = ({
  isReported,
  status,
}: TeachingReviewNoticeProps) => {
  const notice = isReported ? null : getNotice(status);

  if (notice == null) {
    return null;
  }

  const { color, description, Icon, title } = notice;

  return (
    <div
      id="cle-teaching-review-notice"
      style={{ position: "relative" }}
      className="border rounded-3 overflow-hidden fs-6 fw-semibold mb-4 bg-white p-4 d-flex align-items-center gap-2"
      role="status"
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "0.25rem",
          height: "100%",
          backgroundColor: color,
        }}
      />
      <Icon size={26} aria-hidden style={{ color, flexShrink: 0 }} />
      <div className="d-flex flex-column">
        <p className="mb-0">{title}</p>
        <p style={{ fontSize: "0.85rem" }} className="mb-0 opacity-75">
          {description}
        </p>
      </div>
    </div>
  );
};

export default TeachingReviewNotice;
